import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import TableChartIcon from "@mui/icons-material/TableChart";

const DatabaseSection = () => {
  const [dbStats, setDbStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTable, setSelectedTable] = useState(null);
  const [tableDetails, setTableDetails] = useState(null);
  const [tableLoading, setTableLoading] = useState(false);

  useEffect(() => {
    fetchDbStats();
  }, []);

  const fetchDbStats = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/database/stats`
      );
      setDbStats(response.data);
    } catch (err) {
      console.error("Failed to fetch database stats:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTableDetails = async (tableName) => {
    setTableLoading(true);
    setSelectedTable(tableName);
    window.history.pushState(null, "", `/admin/dashboard/database?table=${tableName}`);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/database/table/${tableName}`
      );
      setTableDetails(response.data);
    } catch (err) {
      console.error("Failed to fetch table details:", err);
    } finally {
      setTableLoading(false);
    }
  };

  // Auto-open table from URL on mount
  useEffect(() => {
    if (dbStats) {
      const params = new URLSearchParams(window.location.search);
      const table = params.get("table");
      if (table && !selectedTable) fetchTableDetails(table);
    }
  }, [dbStats]);

  const filteredTables = dbStats?.tables?.filter((table) =>
    table.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress sx={{ color: "#fff" }} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Stats Summary */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <Paper
            sx={{
              p: 2,
              backgroundColor: "#16213e",
              borderRadius: 2,
              border: "1px solid #394867",
              textAlign: "center",
            }}
          >
            <Typography variant="h3" sx={{ color: "#4caf50", fontWeight: 700 }}>
              {dbStats?.totalTables || 0}
            </Typography>
            <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
              Total Tables
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper
            sx={{
              p: 2,
              backgroundColor: "#16213e",
              borderRadius: 2,
              border: "1px solid #394867",
              textAlign: "center",
            }}
          >
            <Typography variant="h3" sx={{ color: "#2196f3", fontWeight: 700 }}>
              {dbStats?.totalRows?.toLocaleString() || 0}
            </Typography>
            <Typography variant="body2" sx={{ color: "#9e9e9e" }}>
              Total Rows
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Search Bar */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: "#16213e",
          borderRadius: 2,
          border: "1px solid #394867",
        }}
      >
        <TextField
          fullWidth
          placeholder="Search tables..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9e9e9e" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              color: "#fff",
              "& fieldset": { borderColor: "#394867" },
              "&:hover fieldset": { borderColor: "#541b14" },
              "&.Mui-focused fieldset": { borderColor: "#541b14" },
            },
            "& .MuiInputBase-input::placeholder": { color: "#666" },
          }}
        />
      </Paper>

      {/* Tables List */}
      <Paper
        sx={{
          backgroundColor: "#16213e",
          borderRadius: 2,
          border: "1px solid #394867",
          overflow: "hidden",
        }}
      >
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: "#0f1729",
                    color: "#9e9e9e",
                    fontWeight: 600,
                    borderBottom: "1px solid #394867",
                  }}
                >
                  Table Name
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    backgroundColor: "#0f1729",
                    color: "#9e9e9e",
                    fontWeight: 600,
                    borderBottom: "1px solid #394867",
                  }}
                >
                  Row Count
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: "#0f1729",
                    color: "#9e9e9e",
                    fontWeight: 600,
                    borderBottom: "1px solid #394867",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTables?.map((table) => (
                <TableRow
                  key={table.name}
                  hover
                  sx={{
                    "&:hover": { backgroundColor: "#1a1a2e" },
                    backgroundColor:
                      selectedTable === table.name ? "#1a1a2e" : "transparent",
                  }}
                >
                  <TableCell
                    sx={{ color: "#fff", borderBottom: "1px solid #394867" }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <TableChartIcon sx={{ color: "#9e9e9e", fontSize: 18 }} />
                      {table.name}
                    </Box>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: "#fff", borderBottom: "1px solid #394867" }}
                  >
                    <Chip
                      label={table.rowCount.toLocaleString()}
                      size="small"
                      sx={{
                        backgroundColor:
                          table.rowCount > 0 ? "#1b5e20" : "#424242",
                        color: "#fff",
                      }}
                    />
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ borderBottom: "1px solid #394867" }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => fetchTableDetails(table.name)}
                      sx={{
                        color: "#2196f3",
                        borderColor: "#2196f3",
                        "&:hover": {
                          borderColor: "#2196f3",
                          backgroundColor: "rgba(33, 150, 243, 0.1)",
                        },
                      }}
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Table Details */}
      {selectedTable && (
        <Paper
          sx={{
            mt: 3,
            p: 3,
            backgroundColor: "#16213e",
            borderRadius: 2,
            border: "1px solid #394867",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ color: "#fff" }}>
              {selectedTable}
              <Chip
                label={`${tableDetails?.rowCount?.toLocaleString() || 0} rows`}
                size="small"
                sx={{ ml: 2, backgroundColor: "#1b5e20", color: "#fff" }}
              />
            </Typography>
            <Button
              size="small"
              onClick={() => {
                setSelectedTable(null);
                setTableDetails(null);
              }}
              sx={{ color: "#9e9e9e" }}
            >
              Close
            </Button>
          </Box>

          {tableLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress sx={{ color: "#fff" }} size={24} />
            </Box>
          ) : (
            <>
              {/* Columns */}
              <Typography
                variant="subtitle2"
                sx={{ color: "#9e9e9e", mb: 1, mt: 2 }}
              >
                Columns ({tableDetails?.columns?.length || 0})
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                {tableDetails?.columns?.map((col) => (
                  <Chip
                    key={col.column_name}
                    label={`${col.column_name} (${col.data_type})`}
                    size="small"
                    sx={{
                      backgroundColor: "#1a1a2e",
                      color: "#fff",
                      border: "1px solid #394867",
                    }}
                  />
                ))}
              </Box>

              {/* Sample Data */}
              <Typography variant="subtitle2" sx={{ color: "#9e9e9e", mb: 1 }}>
                Recent Data (up to 50 rows)
              </Typography>
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {tableDetails?.columns?.map((col) => (
                        <TableCell
                          key={col.column_name}
                          sx={{
                            backgroundColor: "#0f1729",
                            color: "#9e9e9e",
                            fontWeight: 600,
                            borderBottom: "1px solid #394867",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {col.column_name}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tableDetails?.sampleData?.map((row, idx) => (
                      <TableRow key={idx} hover>
                        {tableDetails?.columns?.map((col) => (
                          <TableCell
                            key={col.column_name}
                            sx={{
                              color: "#fff",
                              borderBottom: "1px solid #394867",
                              maxWidth: 200,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row[col.column_name] === null
                              ? <span style={{ color: "#666" }}>NULL</span>
                              : typeof row[col.column_name] === "object"
                              ? JSON.stringify(row[col.column_name]).substring(0, 50) + "..."
                              : String(row[col.column_name]).substring(0, 100)}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    {tableDetails?.sampleData?.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={tableDetails?.columns?.length || 1}
                          sx={{
                            color: "#666",
                            textAlign: "center",
                            borderBottom: "1px solid #394867",
                          }}
                        >
                          No data in this table
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default DatabaseSection;
